import dayjs from "dayjs";

/**
 * One Epson ePOS receipt printer for BOTH screens.
 *
 *  - Sale invoice : printReceipt(...same args as before...)                      -> docType defaults to "INVOICE"
 *  - Sale return  : printReceipt(...same args..., { docType: "RETURN" })
 *
 * The last argument is optional:
 *   options.docType : "INVOICE" (default) | "RETURN"
 *   options.onError : (text) => void   called when the printer cannot be reached
 *
 * Differences for RETURN: title "SALE RETURN", label "RETURN NO", payment lines
 * say REFUND, and the UPI QR code is never printed.
 */
export const printReceipt = (
  localIp,
  address,
  address1,
  city,
  gstNo,
  userPhone,
  nextInvNo,
  invNo,
  billDate,
  userDetails,
  customerName,
  userName,
  customerMobile,
  barCodeData,
  totalAmount,
  totalDisAmt,
  totalNetAmt,
  totalQty,
  base64Images,
  base64QR,
  totalGstData,
  ins,
  ins1,
  ins2,
  ins3,
  ins4,
  ins5,
  ins6,
  gstValue,
  cashAmt,
  cardAmt,
  onlineAmt,
  upiAmt,
  creditAmt,
  options = {},
) => {
  const isReturn = options?.docType === "RETURN";
  const onError = options?.onError;

  const fail = (text, detail) => {
    console.log(text, detail ?? "");
    if (typeof onError === "function") onError(text);
  };

  if (!window.epson) {
    fail("Epson ePOS SDK is not loaded on this page.");
    return;
  }

  var printer = null;
  var ePosDev = new window.epson.ePOSDevice();

  ePosDev.connect(localIp, 8008, cbConnect);

  function cbConnect(data) {
    if (data === "OK" || data === "SSL_CONNECT_OK") {
      ePosDev.createDevice(
        "local_printer",
        ePosDev.DEVICE_TYPE_PRINTER,
        { crypto: true, buffer: false },
        cbCreateDevice_printer,
      );
    } else {
      fail("Could not connect to the printer. Check the printer IP and Wi-Fi.", data);
    }
  }

  function cbCreateDevice_printer(devobj, retcode) {
    if (retcode === "OK") {
      printer = devobj;
      executeAddedCode();
    } else {
      fail("Could not open the printer.", retcode);
    }
  }

  async function executeAddedCode() {
    const lineWidth = 42;
    const num = (v) => Number(v) || 0;
    const showValue = (val) =>
      val && val !== "-" && String(val).trim() !== "" && val !== "undefined"
        ? val
        : "-";
    const isValidValue = (val) =>
      val !== null &&
      val !== undefined &&
      val !== "-" &&
      String(val).trim() !== "" &&
      val !== "undefined";

    // the helper calls .toFixed, and EditItemDetails stores strings
    const items = (barCodeData || []).map((it) => ({
      ...it,
      stock: num(it.stock),
      CPrice: num(it.CPrice),
      Disper: num(it.Disper),
      Disamt: num(it.Disamt),
      offer: num(it.offer),
    }));
    const gstRows = totalGstData || [];
    const tAmount = num(totalAmount);
    const tDisc = num(totalDisAmt);
    const tNet = num(totalNetAmt);

    printer.addTextAlign(printer.ALIGN_CENTER);
    const loadImage = (base64, name) =>
      new Promise((resolve, reject) => {
        if (!base64) {
          reject(`${name} is empty`);
          return;
        }

        const img = new Image();

        img.onload = () => resolve(img);

        img.onerror = (e) => {
          console.error(`${name} load failed`, e);
          reject(`${name} invalid base64`);
        };

        // ensure the data-url prefix
        if (!base64.startsWith("data:image")) {
          img.src = `data:image/png;base64,${base64}`;
        } else {
          img.src = base64;
        }
      });
    let logoImg = null;
    let qrImg = null;

    try {
      logoImg = await loadImage(base64Images, "Logo Image");
    } catch (err) {
      console.warn("Logo skipped:", err);
    }

    // a UPI payment QR makes no sense on a return receipt
    if (!isReturn) {
      try {
        qrImg = await loadImage(base64QR, "QR Image");
      } catch (err) {
        console.warn("QR skipped:", err);
      }
    }

    if (logoImg) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const width = 200;
      const height = Math.round(width * (logoImg.height / logoImg.width));

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(logoImg, 0, 0, width, height);

      printer.addImage(
        ctx,
        0,
        0,
        width,
        height,
        printer.COLOR_1,
        printer.MODE_MONO,
        printer.HALFTONE_DITHER,
        1,
      );

      printer.addFeedLine(1);
    }
    printer.addTextSize(2, 2); // Large font
    printer.addText(showValue(userName) + "\n");
    printer.addTextSize(1, 1); // Reset font size

    printer.addText(showValue(address) + "\n");
    printer.addText(showValue(address1) + "\n");
    printer.addText("PHONE NO: " + showValue(userPhone) + "\n");
    if (isValidValue(gstNo)) {
      printer.addText("GSTIN: " + gstNo + "\n");
    }
    printer.addFeedLine(1);

    // ==== TITLE ====
    const title = isReturn
      ? "SALE RETURN"
      : gstValue === true
        ? "TAX INVOICE"
        : "BILL OF SUPPLY";
    printer.addText("========================================\n");
    printer.addTextFont(printer.FONT_C);
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addTextSize(2, 1);
    printer.addText(title + "\n");
    printer.addTextFont(printer.FONT_A);
    printer.addTextSize(1, 1);
    printer.addText("========================================\n");

    // ==== DOCUMENT INFO ====
    const invLabel = isReturn ? "RETURN NO: " : "INV NO   : ";
    const invValue = String(invNo ? invNo : nextInvNo);
    const invText = invLabel + invValue.padEnd(9, " ");

    const dateLabel = "DATE : ";
    const dateValue = billDate
      ? dayjs(billDate).format("DD-MMM-YYYY hh:mm A")
      : dayjs().format("DD-MMM-YYYY hh:mm A");
    const dateText = dateLabel + dateValue.padStart(11, " ");

    const spaceCounts = lineWidth - (invText.length + dateText.length);
    const space = " ".repeat(spaceCounts > 0 ? spaceCounts : 1);

    printer.addText(invText + space + dateText + "\n");

    const custMobile =
      showValue(userDetails?.Mobileno) !== "-"
        ? userDetails?.Mobileno
        : showValue(customerMobile);
    const custName =
      showValue(userDetails?.Custname) !== "-"
        ? userDetails?.Custname
        : showValue(customerName);

    printer.addTextAlign(printer.ALIGN_LEFT);
    printer.addText("MOBILE   : " + custMobile + "\n");
    printer.addText("CUST NAME: " + custName + "\n");
    printer.addText("------------------------------------------------\n");

    // ==== TABLE HEADER ====
    printer.addText("SNO PARTICULARS    QTY     RATE   DISC    AMOUNT\n");
    printer.addText("------------------------------------------------\n");

    // ==== ITEMS ====
    items.forEach((item, index) => {
      const sno = (index + 1).toString().padEnd(4, " ");
      const code = (item.DesignCode || "").substring(0, 14).padEnd(14, " ");
      const qty = String(item.stock).padStart(4, " ");
      const rate = item.CPrice.toFixed(2).padStart(9, " ");
      const disc = item.Disamt.toFixed(2).padStart(7, " ");
      const amount = (item.stock * item.offer).toFixed(2).padStart(10, " ");

      // Main row (aligned with header)
      printer.addText(sno);
      printer.addTextStyle(true, false, false, printer.COLOR_BLACK);
      printer.addText(code);
      printer.addTextStyle(false, false, false, printer.COLOR_BLACK);
      printer.addText(`${qty}${rate}${disc}${amount}\n`);

      // Second row: product name + discount %
      const name = (item.ProdName || "").substring(0, 30).padEnd(30, " ");
      const size =
        item?.Prodsize && item.Prodsize !== "-" ? `SZ:${item.Prodsize}` : "";
      const hsnCode =
        item?.HSNCODE && item.HSNCODE !== "-" ? `HSN:${item.HSNCODE}` : "";
      // the rate actually applied on screen, falling back to the stored one
      const gstRate = num(item?.GSTApplied ?? item?.GST);
      const gst = gstRate > 0 ? `GST:${gstRate}%` : "";
      const smCode =
        item?.SMCODE && item.SMCODE !== "-" ? `SM:${item.SMCODE}` : "";
      const discPer = item.Disper > 0 ? `${item.Disper}%` : "";
      printer.addTextFont(printer.FONT_A);
      printer.addTextSize(1, 1);
      printer.addText(`    ${name}${discPer}\n`);

      // Third row: HSN, GST, SM, size
      printer.addTextFont(printer.FONT_C);
      printer.addTextSize(1, 1);
      printer.addText(`     ${hsnCode} ${gst} ${smCode} ${size}\n`);

      // Separator
      printer.addTextFont(printer.FONT_A);
      printer.addTextSize(1, 1);
      printer.addFeedLine(1);
    });
    printer.addText("------------------------------------------------\n");

    // ==== SUMMARY ====
    const leftLabel = "TOT QTY : ";
    const leftValue = String(totalQty ?? 0);
    const leftText = leftLabel + leftValue.padEnd(10, " ");
    const rightLabel = "TOTAL AMOUNT : ";
    const rightValue = tAmount.toFixed(2);
    const rightText = rightLabel + rightValue.padStart(12, " ");

    const spaceCount = lineWidth - (leftText.length + rightText.length);
    const spaces = " ".repeat(spaceCount > 0 ? spaceCount : 1);

    printer.addTextStyle(false, false, false, printer.COLOR_BLACK);
    printer.addText(leftText + spaces + rightText + "\n");
    printer.addTextAlign(printer.ALIGN_RIGHT);
    printer.addText("DISCOUNT : " + tDisc.toFixed(2).padStart(12, " ") + "\n");
    printer.addText(
      (isReturn ? "RETURN AMOUNT : " : "NET AMOUNT : ") +
      tNet.toFixed(2).padStart(12, " ") +
      "\n",
    );
    printer.addFeedLine(1);

    // ==== GST SUMMARY (only when the firm charges GST) ====
    if (gstValue === true && gstRows.length > 0) {
      printer.addTextFont(printer.FONT_C);
      printer.addTextAlign(printer.ALIGN_CENTER);
      printer.addTextSize(2, 1);
      printer.addText("GST SUMMARY\n");
      printer.addTextAlign(printer.ALIGN_LEFT);
      printer.addTextFont(printer.FONT_C);
      printer.addTextSize(1, 1);
      printer.addText(
        "----------------------------------------------------------------\n",
      );
      printer.addText(
        "BASIC AMT    CGST(%)  CGST(AMT)    SGST(%)  SGST(AMT)    NET AMT\n",
      );
      printer.addText(
        "----------------------------------------------------------------\n",
      );

      gstRows.forEach((row) => {
        const line =
          num(row.Basic_amt).toFixed(2).padStart(9, " ") +
          String(row.CGST_RATE + "%").padStart(11, " ") +
          num(row.CGST_AMT).toFixed(2).padStart(11, " ") +
          String(row.SGST + "%").padStart(11, " ") +
          num(row.SGST_AMT).toFixed(2).padStart(11, " ") +
          num(row.NET_AMT).toFixed(2).padStart(11, " ");
        printer.addText(line + "\n");
      });
    }

    printer.addTextFont(printer.FONT_A);
    printer.addTextSize(1, 1);
    printer.addText("------------------------------------------------\n");

    // ==== PAYMENT ====
    const paidWord = isReturn ? "REFUND" : "PAID";
    if (num(cashAmt) > 0) {
      printer.addText(
        `CASH ${paidWord} : ` + num(cashAmt).toFixed(2).padStart(12, " ") + "\n",
      );
    }
    if (num(cardAmt) > 0) {
      printer.addText(
        `CARD ${paidWord} : ` + num(cardAmt).toFixed(2).padStart(12, " ") + "\n",
      );
    }
    if (num(upiAmt) > 0) {
      printer.addText(
        `UPI ${paidWord} : ` + num(upiAmt).toFixed(2).padStart(12, " ") + "\n",
      );
    }
    if (num(onlineAmt) > 0) {
      printer.addText(
        `ONLINE ${paidWord} : ` +
        num(onlineAmt).toFixed(2).padStart(12, " ") +
        "\n",
      );
    }
    if (num(creditAmt) > 0) {
      printer.addText(
        "CREDIT : " + num(creditAmt).toFixed(2).padStart(12, " ") + "\n",
      );
    }
    printer.addTextFont(printer.FONT_A);
    printer.addTextSize(1, 1);
    printer.addFeedLine(1);

    // ==== TERMS & CONDITIONS ====
    const terms = [ins, ins1, ins2, ins3, ins4, ins5, ins6].filter(isValidValue);
    if (terms.length > 0) {
      printer.addTextAlign(printer.ALIGN_CENTER);
      printer.addTextSize(1, 1);
      printer.addText("****Terms & Conditions****\n");
      printer.addFeedLine(1);
      printer.addTextAlign(printer.ALIGN_LEFT);
      printer.addTextStyle(false, false, false, printer.COLOR_BLACK);
      printer.addTextFont(printer.FONT_C);
      printer.addTextSize(1, 1);
      terms.forEach((t) => printer.addText(t + "\n"));
      printer.addTextFont(printer.FONT_A);
      printer.addFeedLine(1);
    }

    // ==== UPI QR (invoice only) ====
    printer.addTextAlign(printer.ALIGN_CENTER);
    if (num(upiAmt) > 0 && qrImg) {
      const qrCanvas = document.createElement("canvas");
      const qrCtx = qrCanvas.getContext("2d");

      const qrWidth = 300;
      const qrHeight = Math.round(qrWidth * (qrImg.height / qrImg.width));

      qrCanvas.width = qrWidth;
      qrCanvas.height = qrHeight;

      qrCtx.drawImage(qrImg, 0, 0, qrWidth, qrHeight);

      printer.addImage(
        qrCtx,
        0,
        0,
        qrWidth,
        qrHeight,
        printer.COLOR_1,
        printer.MODE_MONO,
        printer.HALFTONE_DITHER,
        1,
      );

      printer.addFeedLine(1);
    }
    printer.addTextSize(1, 1);

    // ==== FOOTER ====
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addText("Thank you for shopping please visit again!\n");
    printer.addFeedLine(1);
    printer.addTextFont(printer.FONT_C);
    printer.addTextSize(1, 1);
    printer.addTextAlign(printer.ALIGN_LEFT);
    printer.addText("PRINT TIME : " + dayjs().format("hh:mm:ss A") + "\n");
    printer.addFeedLine(2);
    printer.addCut(printer.CUT_FEED);

    // ---- SEND TO PRINTER ----
    printer.send();
  }
};